// src/pages/public/BookingConfirmedPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { db } from "../../config/firebase";
import { doc, getDoc, Timestamp } from "firebase/firestore";

/* --- simple mobile hook (same pattern as other pages) --- */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const handler = () => {
      if (typeof window === "undefined") return;
      setIsMobile(window.innerWidth <= breakpoint);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);

  return isMobile;
}

export default function BookingConfirmedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();
  const isMobile = useIsMobile();

  // Booking id can come from state (preferred) or ?id=...
  const bookingId = location.state?.bookingId || sp.get("id") || "";

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(!!bookingId);
  const [error, setError] = useState("");

  const hero =
    "https://assets.api.uizard.io/api/cdn/stream/597fae2c-5bf0-46f2-ac43-e83205eb5d64.png";

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      if (!bookingId) {
        setError("Missing booking id.");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, "bookings", bookingId));
        if (!ignore) {
          if (snap.exists()) setBooking({ id: snap.id, ...(snap.data() || {}) });
          else setError("Booking not found.");
        }
      } catch (e) {
        if (!ignore) setError("Failed to load booking.");
        console.error(e);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [bookingId]);

  const startDate = useMemo(() => {
    const v = booking?.start;
    if (!v) return null;
    if (v instanceof Timestamp) return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "string" || typeof v === "number") return new Date(v);
    return null;
  }, [booking]);

  const whenText = useMemo(() => {
    if (!startDate) return "—";
    const dateStr = new Intl.DateTimeFormat(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(startDate);
    const timeStr = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }).format(startDate);
    return `${dateStr} at ${timeStr}`;
  }, [startDate]);

  const services = booking?.services || [];
  const staffName =
    booking?.staffName ||
    (booking?.staffId === "any" ? "Any staff" : booking?.staffId || "—");

  const totalLabel = useMemo(
    () => formatCAD(Number(booking?.totalPrice ?? 0)),
    [booking?.totalPrice]
  );
  const durLabel = useMemo(
    () => formatDuration(Number(booking?.totalMin ?? 0)),
    [booking?.totalMin]
  );

  const heroBoxStyle = isMobile ? sx.heroBoxMobile : sx.heroBoxDesktop;
  const containerStyle = isMobile ? sx.containerMobile : sx.containerDesktop;

  return (
    <div style={sx.screen}>
      {/* Header */}
      <div style={sx.header} />

      {/* Hero */}
      <div style={heroBoxStyle}>
        <div style={{ ...sx.heroImage, backgroundImage: `url(${hero})` }} />
        <div
          style={{
            ...sx.heroOverlay,
            ...(isMobile ? sx.heroOverlayMobile : {}),
          }}
        >
          <div style={{ display: "grid", gap: 6 }}>
            <div style={sx.heroTitle}>613Nail Hub</div>
            <div style={sx.heroMeta}>
            📍 598 Rideau St, Ottawa, ON K1N 6C6 <br></br> ☎︎ (613) 327-4049
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={containerStyle}>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div
            style={{
              ...sx.card,
              ...(isMobile ? sx.cardMobile : {}),
            }}
          >
            <div style={sx.title}>Booking Confirmed!</div>

            {loading && (
              <div style={sx.subtitle}>Loading your appointment…</div>
            )}
            {!loading && error && (
              <div style={{ ...sx.subtitle, color: "#b91c1c" }}>{error}</div>
            )}

            {!loading && !error && (
              <>
                <div style={sx.subtitle}>
                  Thank you for booking with 613Nail Hub. We look forward to
                  seeing you!
                </div>

                <div style={sx.detailGrid}>
                  <DetailRow
                    label="When"
                    value={whenText}
                    isMobile={isMobile}
                  />
                  <DetailRow
                    label="With"
                    value={staffName}
                    isMobile={isMobile}
                  />
                  <DetailRow
                    label="Location"
                    value="598 Rideau St, Ottawa, ON K1N 6C6"
                    isMobile={isMobile}
                  />
                  <DetailRow
                    label="Total"
                    value={`${totalLabel} • ${durLabel}`}
                    isMobile={isMobile}
                  />
                </div>

                {/* Services list */}
                <div style={{ marginTop: 16 }}>
                  {services.map((s) => (
                    <div key={s.id || s.name} style={sx.serviceRow}>
                      <div style={sx.serviceName}>{s.name || "Service"}</div>
                      <div style={sx.serviceMeta}>
                        {formatDuration(s.durationMin || s.duration || 0)} •{" "}
                        {formatCAD(Number(s.price || 0))}
                        {s.type?.toLowerCase?.() === "from" ? " (From)" : ""}
                      </div>
                    </div>
                  ))}
                  {services.length === 0 && (
                    <div
                      style={{ color: "#6b7280", fontSize: 14 }}
                    >
                      No services listed.
                    </div>
                  )}
                </div>
              </>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 24,
              }}
            >
              <button
                style={{
                  ...sx.addBtn,
                  ...(isMobile ? { width: "100%" } : {}),
                }}
                onClick={() => navigate("/")}
              >
                Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, isMobile }) {
  return (
    <div
      style={{
        ...sx.detailRow,
        ...(isMobile ? sx.detailRowMobile : {}),
      }}
    >
      <div style={sx.detailLabel}>{label}:</div>
      <div style={sx.detailValue}>{value}</div>
    </div>
  );
}

/* helpers */
function formatCAD(n) {
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch {
    return `C$ ${n}`;
  }
}
function formatDuration(min) {
  const m = Number(min) || 0;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
}

/* ---------------- Styles ---------------- */
const sx = {
  screen: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    fontFamily:
      "Work Sans, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  header: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "18px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  // hero wrappers
  heroBoxDesktop: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 20px",
    position: "relative",
  },
  heroBoxMobile: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "0 16px",
    position: "relative",
  },
  heroImage: {
    width: "100%",
    height: 210,
    borderRadius: 4,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  heroOverlay: {
    position: "absolute",
    left: 40,
    top: 64,
    display: "flex",
    gap: 12,
    alignItems: "center",
    background: "rgba(113,93,89,0.85)",
    padding: "10px 14px",
    borderRadius: 10,
    color: "#fff",
  },
  heroOverlayMobile: {
    left: 16,
    top: 40,
    right: 16,
  },
  badgeNum: { fontSize: 36, fontWeight: 300, lineHeight: 1, padding: "6px 10px" },
  heroTitle: { fontSize: 18, fontWeight: 700 },
  heroMeta: { fontSize: 13, fontWeight: 500, opacity: 0.95 },

  // containers
  containerDesktop: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "24px 20px 48px",
  },
  containerMobile: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "24px 16px 40px",
  },

  card: {
    width: "100%",
    maxWidth: 960,
    backgroundColor: "#ffffff",
    borderRadius: 14,
    border: "1px solid #d3d6d4",
    boxSizing: "border-box",
    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
    padding: 24,
  },
  cardMobile: {
    maxWidth: "100%",
    padding: 20,
  },

  title: {
    color: "#5c5470",
    fontSize: 30,
    fontWeight: 700,
    lineHeight: "36px",
    textAlign: "center",
  },
  subtitle: {
    color: "#6b7280",
    fontSize: 18,
    lineHeight: "28px",
    textAlign: "center",
    marginTop: 8,
  },

  detailGrid: {
    marginTop: 20,
    display: "grid",
    gap: 12,
  },
  detailRow: {
    display: "grid",
    gridTemplateColumns: "140px 1fr",
    alignItems: "center",
    padding: "10px 12px",
    borderRadius: 8,
    background: "#fafafa",
  },
  detailRowMobile: {
    gridTemplateColumns: "1fr",
    rowGap: 4,
  },
  detailLabel: {
    color: "#4b5563",
    fontSize: 14,
    fontWeight: 700,
  },
  detailValue: {
    color: "#4b5563",
    fontSize: 14,
  },

  serviceRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 12px",
    borderBottom: "1px dashed #eee",
  },
  serviceName: { fontSize: 15, fontWeight: 700, color: "#030303" },
  serviceMeta: { fontSize: 13, color: "#6b7280", fontWeight: 600 },

  addBtn: {
    cursor: "pointer",
    minWidth: 140,
    height: 46,
    border: 0,
    borderRadius: 8,
    backgroundColor: "#ff9d9b",
    color: "#ffffff",
    fontSize: 16,
    fontWeight: 700,
    lineHeight: "28px",
    padding: "0 16px",
  },
  secondaryBtn: {
    cursor: "pointer",
    minWidth: 100,
    height: 46,
    border: 0,
    borderRadius: 8,
    backgroundColor: "#e7e9ee",
    color: "#334155",
    fontSize: 15,
    fontWeight: 700,
    padding: "0 16px",
  },
};
